// src/components/Form/attendance.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { FaArrowLeft, FaSave, FaTimes, FaUser, FaCalendarAlt, FaClock, FaEdit, FaCheck, FaTimesCircle, FaStickyNote, FaEnvelope, FaIdCard, FaCog, FaExclamationTriangle } from 'react-icons/fa';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import './CreateCustomerPage.css';
const AttendanceCreate = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const id = searchParams.get('id');
  const paramEmployeeId = searchParams.get('employee_id');
  const paramDate = searchParams.get('date');
  const containerRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [warning, setWarning] = useState(null);


  // Global State
  const { baseUrl, configLoading } = useContext(UserContext);

  const [isEdit, setIsEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [dailyRecords, setDailyRecords] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);

  // Permission State
  const [canWrite, setCanWrite] = useState(false);
  const [dataScope, setDataScope] = useState("All Data");
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [userBranch, setUserBranch] = useState("");
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);

  // Helper to get headers with tenancy
  const getHeaders = (branch = null, comp = null) => {
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    const activeContext = localStorage.getItem('active_company');
    const token = localStorage.getItem('token');
    
    // Authorization
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // STRICT Multi-Tenant Isolation:
    // 1. Resolve Company from active context or fallback to user default
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const defaultCompany = user.company_name || user.company;
    
    const resolvedCompany = comp || ((activeContext && activeContext !== 'All') ? activeContext : defaultCompany);
    if (resolvedCompany) headers['X-Company-Name'] = resolvedCompany;
    
    // 2. Resolve Branch from Filter or active context
    const branchToUse = branch || (selectedBranchFilter && selectedBranchFilter !== 'All Branches' ? selectedBranchFilter : null);
    if (branchToUse && branchToUse !== 'All Branches') headers['X-Branch-Name'] = branchToUse;
    
    return headers;
  };

  // Initial Data Fetching & Permissions
  useEffect(() => {
    // Wait for config to load
    if (configLoading) return;

    const initData = async () => {
      setLoading(true);
      try {
        const scope = await fetchPermissions();
        await fetchData(scope);

        if (id && !location.state?.attendance) {
          await fetchSingleAttendance(id);
        }
      } catch (err) {
        console.error("Error initializing data:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchPermissions = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          const role = userObj.role;
          setIsCompanyAdmin(role === 'company_admin' || role === 'admin');
          setIsGroupAdmin(role === 'group_admin' || role === 'Super Admin' || userObj.isGlobalAdmin === true);
          setUserBranch(userObj.branch_name || userObj.branch || "");
          if (role) {
            const currentBaseUrl = baseUrl || '';
            const userEmail = userObj.email || '';
            const url = `${currentBaseUrl}/api/role-permissions?role=${role}&email=${userEmail}`;
            const response = await axios.get(url, { headers: getHeaders() });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'create_attendance');
            if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              const scope = pagePerm.dataAccess || pagePerm.dataScope || "All Data";
              setDataScope(scope);
              return scope;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
      return "All Data";
    };

    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/branches`, { headers: getHeaders() });
        const branchData = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
        setAvailableBranches(branchData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    initData();
    fetchBranches();
  }, [baseUrl, configLoading, id, location.state, paramEmployeeId, paramDate, selectedBranchFilter]);

  // Helper to process attendance data for form
  const processAttendanceData = (att) => {
    const data = { ...att };
    if (data.employee && data.employee._id) {
      data.employee_id = data.employee._id;
    } else if (!data.employee_id && att.employee_id) {
      data.employee_id = att.employee_id;
    }
    // Ensure numeric fields
    ['worked_minutes', 'overtime_minutes', 'late_minutes', 'early_exit_minutes'].forEach(field => {
      data[field] = Number(data[field]) || 0;
    });
    data.is_overnight = Boolean(data.is_overnight);
    data.is_roster = Boolean(data.is_roster);
    return data;
  };

  const [formData, setFormData] = useState(() => {
    if (location.state?.attendance) {
      return processAttendanceData(location.state.attendance);
    }
    return {
      _id: '',
      employee_id: paramEmployeeId || '',
      attendance_date: paramDate || '',
      schedule_id: '',
      shift_id: '',
      status: 'Present',
      planned_start_time: '',
      planned_end_time: '',
      actual_check_in: '',
      actual_check_out: '',
      worked_minutes: 0,
      overtime_minutes: 0,
      late_minutes: 0,
      early_exit_minutes: 0,
      is_overnight: false,
      is_roster: false,
      special_day_type: 'None',
      notes: ''
    };
  });

  // Set isEdit and autoFilled based on initial state
  useEffect(() => {
    if (location.state?.attendance) {
      // If it has an _id and is not virtual, it's an edit
      if (location.state.attendance._id && !location.state.attendance.is_virtual) {
        setIsEdit(true);
        setAutoFilled(true);
      } else {
        // Virtual record or new entry pre-filled
        setAutoFilled(true);
        if (location.state.attendance.is_virtual) {
          setFormData(prev => ({ ...prev, _id: '' })); // Ensure no ID for creation
          setIsEdit(false);
        }
      }
    }
  }, [location.state]);

  // Fetch employees, schedules, shifts
  const fetchData = async (currentScope = dataScope) => {
    try {
      setError(null);
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : null;
      const empRes = await axios.get(`${baseUrl}/api/add-employee`, {
        params: {
          userId: user?.userId || user?._id || user?.id,
          role: user?.role,
          pageId: 'create_attendance',
          branch_name: selectedBranchFilter
        },
        headers: getHeaders()
      });
      let employeeList = empRes.data || [];
      setEmployees(employeeList);

      const schedRes = await axios.get(`${baseUrl}/api/schedule-rules`, { headers: getHeaders() });
      setSchedules(schedRes.data || []);

      const shiftRes = await axios.get(`${baseUrl}/api/schedules`, { headers: getHeaders() });
      setShifts(shiftRes.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(`Failed to load data: ${err.message}`);
    }
  };

  // Fetch single attendance for edit
  const fetchSingleAttendance = async (attendanceId) => {
    try {
      const url = `${baseUrl}/api/attendance`;
      const response = await axios.get(url, {
        params: {
          _id: attendanceId,
          userId: user?.userId || user?._id,
          role: user?.role
        },
        headers: getHeaders()
      });
      let att = Array.isArray(response.data) ? response.data[0] : response.data;
      if (att) {
        const processed = processAttendanceData(att);
        setFormData(processed);
        setIsEdit(true);
        setAutoFilled(true);
      } else {
        setError('Attendance record not found.');
      }
    } catch (err) {
      console.error('Failed to fetch attendance for edit:', err);
      setError(`Failed to load attendance: ${err.response?.data?.error || err.message}`);
    }
  };

  // Fetch all assignments for employee
  const fetchAssignments = async (employeeId) => {
    try {
      // Use schedule-assignments endpoint which returns all
      const url = `${baseUrl}/api/schedule-assignments`;
      const response = await axios.get(url, {
        params: { userId: user?.userId || user?._id || user?.id, role: user?.role },
        headers: getHeaders()
      });
      // Client-side filter for now
      return response.data.filter(a => String(a.employee_id) === String(employeeId));
    } catch (err) {
      console.error('Failed to fetch assignments:', err);
      return [];
    }
  };

  // Fetch daily schedule (with date)
  const fetchDailySchedule = async (employeeId, date) => {
    try {
      const response = await axios.get(`${baseUrl}/api/daily-schedule?employee_id=${employeeId}&date=${date}`, { headers: getHeaders() });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch daily schedule:', err);
      setError('Failed to load daily schedule settings.');
      return null;
    }
  };

  // Helper to extract correct times from shift object
  const getShiftTimes = (shift) => {
    if (!shift) return { start: '', end: '', isOvernight: false, isSplit: false };
    if (shift.time_slots && shift.time_slots.length > 0) {
      const slots = shift.time_slots;
      const start = slots[0].start_time;
      const end = slots[slots.length - 1].end_time;
      const isOvernight = slots.some(s => s.is_overnight) || shift.is_overnight;
      return {
        start,
        end,
        isOvernight,
        isSplit: slots.length > 1,
        slots
      };
    }
    return {
      start: shift.start_time || '',
      end: shift.end_time || '',
      isOvernight: shift.is_overnight || false,
      isSplit: false
    };
  };

  // Auto-populate on employee_id or attendance_date change
  useEffect(() => {
    const { employee_id, attendance_date, _id } = formData;
    if (!employee_id) return;
    const autoPopulate = async () => {
      const today = new Date().toISOString().split('T')[0];
      const targetDate = attendance_date || today;

      // 1. Fetch Existing Attendance
      let existingRecs = [];
      try {
        const result = await fetchDailySchedule(employee_id, targetDate);
        if (Array.isArray(result)) existingRecs = result;
        // If "result.auto_filled" is true but it's not an array, it means backend returned a projected schedule, not existing records.
        else if (result && result.auto_filled) existingRecs = [];
        setDailyRecords(existingRecs);
      } catch (e) { console.error(e); }

      // 2. Fetch Assignments
      const allAssigns = await fetchAssignments(employee_id);

      // 3. Determine Regular & Roster Assignments
      // A. Roster (Specific Date Match)
      const rosterAssigns = allAssigns.filter(a =>
        a.is_active &&
        a.assigned_date === targetDate &&
        (a.notes || '').includes('Substitute for')
      );

      // B. Regular (Latest Active <= Date)
      const regularAssign = allAssigns
        .filter(a => a.is_active && a.assigned_date <= targetDate && !(a.notes || '').includes('Substitute for'))
        .sort((a, b) => b.assigned_date.localeCompare(a.assigned_date))[0];

      // 4. Generate Timeslots
      let generatedSlots = [];

      const processAssign = (assign, isRoster) => {
        if (!assign) return;
        const schedule = schedules.find(s => s._id === assign.schedule_id);
        if (!schedule) return;

        let shift = shifts.find(s => s._id === (schedule.shift_id || assign.shift_id));
        if (!shift && schedule.time_slots) shift = schedule; // Fallback

        if (!shift) return;

        const { slots: sSlots, start, end } = getShiftTimes(shift);

        if (sSlots && sSlots.length > 0) {
          sSlots.forEach(s => {
            generatedSlots.push({
              start_time: s.start_time,
              end_time: s.end_time,
              is_overnight: s.is_overnight,
              is_roster: isRoster,
              schedule_id: assign.schedule_id,
              shift_id: shift._id,
              label: isRoster ? `Roster: ${s.start_time}-${s.end_time}` : `Regular: ${s.start_time}-${s.end_time}`
            });
          });
        } else {
          generatedSlots.push({
            start_time: start || shift.start_time,
            end_time: end || shift.end_time,
            is_overnight: shift.is_overnight || false,
            is_roster: isRoster,
            schedule_id: assign.schedule_id,
            shift_id: shift._id,
            label: isRoster ? `Roster: ${start}-${end}` : `Regular: ${start}-${end}`
          });
        }
      };

      processAssign(regularAssign, false);
      rosterAssigns.forEach(ra => processAssign(ra, true));

      setTimeSlots(generatedSlots);

      // 5. Select Initial Slot (New vs Edit)
      if (_id) {
        // Editing specific record
        const currentRec = existingRecs.find(r => r._id === _id);
        if (currentRec) {
          const isRosterRec = Boolean(currentRec.is_roster);
          const matchingSlotIdx = generatedSlots.findIndex(s =>
            s.start_time === currentRec.planned_start_time && s.is_roster === isRosterRec
          );
          if (matchingSlotIdx !== -1) setSelectedSlotIndex(matchingSlotIdx);
        }
      } else {
        // Creating New - Find first slot without existing record
        const firstEmpty = generatedSlots.findIndex(s => !existingRecs.some(r => r.planned_start_time === s.start_time && Boolean(r.is_roster) === s.is_roster));

        if (firstEmpty !== -1) {
          handleSlotClick(generatedSlots[firstEmpty], firstEmpty, true);
        } else if (generatedSlots.length > 0) {
          handleSlotClick(generatedSlots[0], 0, true);
        } else {
          // No slots generated? Reset to basics
          setFormData(prev => ({ ...prev, schedule_id: '', shift_id: '' }));
        }
      }
      setAutoFilled(true);
    };
    autoPopulate();
  }, [formData.employee_id, formData.attendance_date, shifts, schedules]);

  // Handle manual shift change
  useEffect(() => {
    if (!autoFilled && formData.shift_id) {
      const shiftObj = shifts.find(s => s._id === formData.shift_id);
      if (shiftObj) {
        const { start, end, isOvernight, isSplit, slots } = getShiftTimes(shiftObj);
        let newNotes = formData.notes;
        if (isSplit && slots) {
          const formattedSlots = slots.map(s => `${s.start_time}-${s.end_time}`).join(', ');
          if (!newNotes.includes('Split Shift')) {
            newNotes = newNotes ? `${newNotes} (Split Shift: ${formattedSlots})` : `Split Shift: ${formattedSlots}`;
          }
        }
        setFormData(prev => ({
          ...prev,
          planned_start_time: start,
          planned_end_time: end,
          is_overnight: isOvernight,
          notes: newNotes
        }));
      }
    }
  }, [formData.shift_id, autoFilled, shifts]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updates = { ...prev, [name]: type === 'checkbox' ? checked : value };

      // Auto-select today's date if employee is selected and date is empty
      if (name === 'employee_id' && value && !prev.attendance_date) {
        updates.attendance_date = new Date().toISOString().split('T')[0];
      }

      return updates;
    });
    setError(null);
    setMessage('');
  };

  // Auto-calculate minutes on check_in/out change
  useEffect(() => {
    const { actual_check_in, actual_check_out, planned_start_time, planned_end_time, status, is_overnight } = formData;
    if (actual_check_in && actual_check_out && planned_start_time && planned_end_time && status === 'Present') {
      const parseTimeToMinutes = (timeStr) => {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };
      let inMins = parseTimeToMinutes(actual_check_in);
      let outMins = parseTimeToMinutes(actual_check_out);
      let planStartMins = parseTimeToMinutes(planned_start_time);
      let planEndMins = parseTimeToMinutes(planned_end_time);
      let adjustedOut = outMins;
      if (is_overnight && outMins < inMins) {
        adjustedOut += 1440;
      }
      const worked = Math.max(0, adjustedOut - inMins);
      const late = Math.max(0, inMins - planStartMins);
      let planDuration = planEndMins - planStartMins;
      if (is_overnight && planEndMins < planStartMins) {
        planDuration = planEndMins + 1440 - planStartMins;
      }
      const overtime = Math.max(0, worked - planDuration);
      let adjustedPlanEnd = planEndMins;
      if (is_overnight && planEndMins < planStartMins) {
        adjustedPlanEnd += 1440;
      }
      const early = Math.max(0, adjustedPlanEnd - adjustedOut);
      setFormData(prev => ({
        ...prev,
        worked_minutes: worked,
        late_minutes: late,
        early_exit_minutes: early,
        overtime_minutes: overtime
      }));
    } else if (status !== 'Present') {
      setFormData(prev => ({
        ...prev,
        worked_minutes: 0,
        overtime_minutes: 0,
        late_minutes: 0,
        early_exit_minutes: 0
      }));
    }
  }, [formData.actual_check_in, formData.actual_check_out, formData.planned_start_time, formData.planned_end_time, formData.status, formData.is_overnight]);

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to create/edit attendance.");
      setTimeout(() => setError(null), 3000);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!formData.employee_id || !formData.attendance_date) {
      setError('Employee and date are required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setMessage('');
    try {
      const url = `${baseUrl}/api/attendance`;
      let response;
      if (isEdit && formData._id) {
        response = await axios.put(url, formData, { headers: getHeaders() });
        setMessage(response.data.message || 'Attendance record updated successfully!');
        // Delay navigation to show message
        setTimeout(() => {
          navigate('/attendance-view');
        }, 2000);
      } else {
        response = await axios.post(url, formData, { headers: getHeaders() });
        setMessage(response.data.message || 'Attendance record created successfully!');
        // Delay navigation to show message, then redirect
        setTimeout(() => {
          navigate('/attendance-view');
        }, 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      if (errorMsg.toLowerCase().includes('record exists') || errorMsg.toLowerCase().includes('already exists')) {
        setWarning(`An attendance record already exists for this employee on this date. (${errorMsg})`);
      } else {
        setError(`Failed to ${isEdit ? 'update' : 'create'} attendance: ${errorMsg}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Slot Selection
  // Handle Slot Selection
  const handleSlotClick = (slot, index, silent = false) => {
    setSelectedSlotIndex(index);
    // Use simple time matching within the already-filtered dailyRecords
    // MUST match by is_roster too
    const existing = dailyRecords.find(r => r.planned_start_time === slot.start_time && Boolean(r.is_roster) === slot.is_roster);

    if (existing) {
      setFormData(processAttendanceData(existing));
      setIsEdit(true);
      setAutoFilled(true);
      if (!silent) {
        setMessage('Loaded existing record for this slot.');
        setTimeout(() => setMessage(''), 1500);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        _id: '',
        schedule_id: slot.schedule_id,
        shift_id: slot.shift_id,
        planned_start_time: slot.start_time,
        planned_end_time: slot.end_time,
        is_overnight: slot.is_overnight,
        is_roster: slot.is_roster,
        status: 'Present',
        actual_check_in: '',
        actual_check_out: '',
        worked_minutes: 0,
        overtime_minutes: 0,
        late_minutes: 0,
        early_exit_minutes: 0,
        notes: slot.is_roster ? (slot.is_roster ? prev.notes : 'Roster Duty') : prev.notes // Simple logic
      }));
      setIsEdit(false);
      if (!silent) {
        setMessage('Switched to new slot. Enter details.');
        setTimeout(() => setMessage(''), 1500);
      }
    }
  };

  const renderSplitSlots = () => {
    if (!timeSlots || timeSlots.length === 0) return null;

    return (
      <div style={{ marginTop: '20px', background: '#f8fafc', padding: '16px', borderRadius: '14px', border: '1.5px solid #e2e8f0' }}>
        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '700', fontSize: '13px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <FaClock style={{ marginRight: '6px' }} /> Available Shifts / Slots:
        </label>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {timeSlots.map((slot, idx) => {
            const isSelected = selectedSlotIndex === idx;
            const existing = dailyRecords.find(r => r.planned_start_time === slot.start_time && Boolean(r.is_roster) === slot.is_roster);
            const statusTitle = existing ? existing.status : 'Empty';
            const statusColor = existing ? '#10b981' : '#94a3b8';

            const borderColor = isSelected ? '#3b82f6' : (slot.is_roster ? '#8b5cf6' : '#cbd5e1');

            return (
              <div
                key={idx}
                onClick={() => handleSlotClick(slot, idx)}
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: `2px solid ${borderColor}`,
                  background: isSelected ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#ffffff',
                  color: isSelected ? '#ffffff' : '#1e293b',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px',
                  boxShadow: isSelected ? '0 10px 20px -5px rgba(59, 130, 246, 0.4)' : '0 2px 5px rgba(0,0,0,0.02)',
                  transition: 'all 0.2s'
                }}
              >
                {slot.is_roster && (
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '800', color: isSelected ? '#fde047' : '#8b5cf6', letterSpacing: '0.5px' }}>
                    Roster Duty
                  </span>
                )}
                <div style={{ fontWeight: '800', fontSize: '15px' }}>{slot.start_time}</div>
                <div style={{ fontSize: '12px', opacity: isSelected ? 0.9 : 0.6, fontWeight: '600' }}>to {slot.end_time}</div>
                <div style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  background: isSelected ? 'rgba(255,255,255,0.2)' : `${statusColor}15`,
                  color: isSelected ? '#ffffff' : statusColor,
                  width: '100%',
                  textAlign: 'center'
                }}>
                  {statusTitle}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#3b82f6' }}>
          <FaClock style={{ fontSize: '48px', marginBottom: '20px', animation: 'spin 2s linear infinite' }} />
          <p style={{ fontWeight: '600', fontSize: '16px', color: '#64748b' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '13px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const inputStyle = { width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', outline: 'none' };
  const disabledInputStyle = { ...inputStyle, background: '#e2e8f0', color: '#64748b', cursor: 'not-allowed' };
  const buttonStyle = { height: '52px', borderRadius: '16px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'all 0.2s', border: 'none' };

  return (
    <div className="create-customer-container main-customer-page" ref={containerRef}>
      <div className="customer-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px', background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
        <div className="header-left" style={{ flex: 1 }}>
          <button type="button" onClick={() => navigate('/attendance-view')} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', color: '#3b82f6', border: '1px solid #e2e8f0', padding: '10px 24px', borderRadius: '50px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <FaArrowLeft /> Back
          </button>
        </div>
        
        <div className="title-wrapper" style={{ flex: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: 0 }}>{isEdit ? 'Edit' : 'Create'} Attendance Record</h2>
        </div>

        <div className="header-right" style={{ flex: 1, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          {(isGroupAdmin || isCompanyAdmin) && (
            <button type="button" onClick={() => setShowCustomizeModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#00d284', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '50px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,210,132,0.2)' }}>
              <FaCog /> Customize
            </button>
          )}
          <button type="button" onClick={() => navigate('/attendance-view')} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', color: '#3b82f6', border: '1px solid #e2e8f0', padding: '10px 24px', borderRadius: '50px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <FaTimes /> Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={isSubmitting} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#00d284', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '50px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,210,132,0.2)' }}>
            <FaSave /> {isSubmitting ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Record' : 'Create Record')}
          </button>
        </div>
      </div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper">
          
          {error && (
            <div style={{ background: '#fef2f2', color: '#ef4444', padding: '16px 20px', borderRadius: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600', border: '1px solid #fee2e2' }}>
              <FaTimesCircle style={{ fontSize: '20px' }} /> {error}
            </div>
          )}
          
          {message && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
              <div style={{ background: '#ffffff', padding: '40px 32px', borderRadius: '24px', width: '90%', maxWidth: '440px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', animation: 'modalScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: '#ecfdf5', color: '#10b981', border: '2px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px', boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.2)' }}>
                  <FaCheck />
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>Success</h3>
                <p style={{ fontSize: '16px', color: '#64748b', margin: '0', fontWeight: '500' }}>{message}</p>
              </div>
            </div>
          )}

          {warning && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
              <div style={{ background: '#ffffff', padding: '40px 32px', borderRadius: '24px', width: '90%', maxWidth: '440px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', animation: 'modalScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: '#fffbeb', color: '#f59e0b', border: '2px solid #fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px', boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.2)' }}>
                  <FaExclamationTriangle />
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>Warning</h3>
                <p style={{ fontSize: '16px', color: '#64748b', margin: '0 0 24px 0', fontWeight: '500' }}>{warning}</p>
                <button type="button" onClick={() => setWarning(null)} style={{ padding: '12px 24px', background: '#f59e0b', color: '#ffffff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', width: '100%' }}>Acknowledge</button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-section-card theme-blue" style={{ marginBottom: '24px' }}>
              <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <div className="section-icon" style={{ background: '#e0e7ff', color: '#4f46e5', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  <FaUser />
                </div>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Employee Details</h2>
              </div>
              
              {(isGroupAdmin || (isCompanyAdmin && !userBranch)) && (
                <div style={{ marginBottom: '24px', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <label style={labelStyle}>Filter by Branch</label>
                  <select value={selectedBranchFilter} onChange={(e) => setSelectedBranchFilter(e.target.value)} style={inputStyle}>
                    <option value="">All Branches</option>
                    {availableBranches.map(branch => <option key={branch} value={branch}>{branch}</option>)}
                  </select>
                </div>
              )}
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={labelStyle}><FaIdCard style={{ marginRight: '6px', opacity: 0.5 }} /> Employee ID</label>
                  <select name="employee_id" value={formData.employee_id} onChange={handleChange} required style={inputStyle}>
                    <option value="">Select ID</option>
                    {employees.map((emp, idx) => <option key={`id-${emp._id || emp.id || idx}`} value={emp._id || emp.id}>{emp.employeeId || 'ID' + (emp._id || emp.id).toString().slice(-4)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}><FaUser style={{ marginRight: '6px', opacity: 0.5 }} /> Employee Name</label>
                  <select name="employee_id" value={formData.employee_id} onChange={handleChange} required style={inputStyle}>
                    <option value="">Select Name</option>
                    {employees.map((emp, idx) => <option key={`name-${emp._id || emp.id || idx}`} value={emp._id || emp.id}>{emp.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}><FaEnvelope style={{ marginRight: '6px', opacity: 0.5 }} /> Employee Email</label>
                  <select name="employee_id" value={formData.employee_id} onChange={handleChange} required style={inputStyle}>
                    <option value="">Select Email</option>
                    {employees.map((emp, idx) => <option key={`email-${emp._id || emp.id || idx}`} value={emp._id || emp.id}>{emp.email}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '16px', fontWeight: '500' }}>* Selecting any field above will automatically sync the others.</div>
            </div>

            {formData.employee_id && (
              <div className="form-section-card theme-blue">
                <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div className="section-icon" style={{ background: '#ecfdf5', color: '#10b981', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                    <FaCalendarAlt />
                  </div>
                  <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Attendance Info</h2>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', alignItems: 'start' }}>
                  
                  {/* Row 1 */}
                  <div>
                    <label style={labelStyle}>Attendance Date</label>
                    <input type="date" name="attendance_date" value={formData.attendance_date} onChange={handleChange} onClick={(e) => { try { e.target.showPicker(); } catch (err) { } }} required style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Shift (Auto)</label>
                    <select name="shift_id" value={formData.shift_id} onChange={handleChange} required disabled={autoFilled} style={autoFilled ? disabledInputStyle : inputStyle}>
                      <option value="">Auto-Selected</option>
                      {shifts.map(s => {
                        let timeDisplay = '';
                        if (s.time_slots && s.time_slots.length > 0) {
                          timeDisplay = s.time_slots.map(ts => `${ts.start_time}-${ts.end_time}`).join(', ');
                        } else {
                          timeDisplay = `${s.start_time || ''} - ${s.end_time || ''}`;
                        }
                        return <option key={s._id} value={s._id}>{s.schedule_name || s.name} ({timeDisplay})</option>
                      })}
                    </select>
                    {renderSplitSlots()}
                  </div>

                  {/* Row 2 */}
                  <div>
                    <label style={labelStyle}>Schedule (Auto)</label>
                    <select name="schedule_id" value={formData.schedule_id} onChange={handleChange} required disabled={autoFilled} style={autoFilled ? disabledInputStyle : inputStyle}>
                      <option value="">Auto-Selected</option>
                      {schedules.map(s => <option key={s._id} value={s._id}>{s.schedule_name || s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Special Day Type (Auto)</label>
                    <select name="special_day_type" value={formData.special_day_type} onChange={handleChange} required disabled={autoFilled} style={autoFilled ? disabledInputStyle : inputStyle}>
                      <option value="None">None</option>
                      {['Holiday', 'Extended', 'HalfDay', 'WeeklyOff'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Row 3 */}
                  <div>
                    <label style={labelStyle}>Status (Auto)</label>
                    <select name="status" value={formData.status} onChange={handleChange} required disabled={autoFilled} style={autoFilled ? disabledInputStyle : inputStyle}>
                      <option value="Present">Present</option>
                      {['Absent', 'Leave', 'Holiday', 'WeeklyOff', 'HalfDay', 'Extended', 'On Leave', 'Paid Leave'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Planned End Time (Auto)</label>
                    <input type="text" placeholder="HH:MM AM/PM" name="planned_end_time" value={formData.planned_end_time} onChange={handleChange} readOnly={autoFilled} style={autoFilled ? disabledInputStyle : inputStyle} />
                  </div>

                  {/* Row 4 */}
                  <div>
                    <label style={labelStyle}>Planned Start Time (Auto)</label>
                    <input type="text" placeholder="HH:MM AM/PM" name="planned_start_time" value={formData.planned_start_time} onChange={handleChange} readOnly={autoFilled} style={autoFilled ? disabledInputStyle : inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Actual Check Out</label>
                    <input type="text" placeholder="HH:MM AM/PM" name="actual_check_out" value={formData.actual_check_out} onChange={handleChange} disabled={['WeeklyOff', 'Holiday', 'Absent', 'Leave', 'On Leave', 'Paid Leave'].includes(formData.status)} style={['WeeklyOff', 'Holiday', 'Absent', 'Leave', 'On Leave', 'Paid Leave'].includes(formData.status) ? disabledInputStyle : inputStyle} />
                  </div>

                  {/* Row 5 */}
                  <div>
                    <label style={labelStyle}>Actual Check In</label>
                    <input type="text" placeholder="HH:MM AM/PM" name="actual_check_in" value={formData.actual_check_in} onChange={handleChange} disabled={['WeeklyOff', 'Holiday', 'Absent', 'Leave', 'On Leave', 'Paid Leave'].includes(formData.status)} style={['WeeklyOff', 'Holiday', 'Absent', 'Leave', 'On Leave', 'Paid Leave'].includes(formData.status) ? disabledInputStyle : inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Overtime Minutes (Auto)</label>
                    <input type="number" name="overtime_minutes" value={formData.overtime_minutes} readOnly style={disabledInputStyle} />
                  </div>

                  {/* Row 6 */}
                  <div>
                    <label style={labelStyle}>Worked Minutes (Auto)</label>
                    <input type="number" name="worked_minutes" value={formData.worked_minutes} readOnly style={disabledInputStyle} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={labelStyle}>Late Mins</label>
                      <input type="number" name="late_minutes" value={formData.late_minutes} readOnly style={disabledInputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Early Exit</label>
                      <input type="number" name="early_exit_minutes" value={formData.early_exit_minutes} readOnly style={disabledInputStyle} />
                    </div>
                  </div>

                </div>

                {/* Additional Settings */}
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: autoFilled ? 'not-allowed' : 'pointer', fontWeight: '600', color: '#1e293b' }}>
                      <input type="checkbox" name="is_overnight" checked={formData.is_overnight} onChange={handleChange} disabled={autoFilled} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                      Is Overnight (Auto)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '600', color: '#1e293b' }}>
                      <input type="checkbox" name="is_roster" checked={formData.is_roster} onChange={handleChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                      Is Roster Duty (Manual)
                    </label>
                  </div>
                  <div>
                    <label style={labelStyle}>Notes (Auto)</label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} readOnly={autoFilled} style={{ ...inputStyle, height: 'auto', padding: '12px 16px', resize: 'vertical', ...(autoFilled ? disabledInputStyle : {}) }} />
                  </div>
                </div>

                {/* Action Buttons Removed */}
              </div>
            )}
          </form>
        </div>
      </div>

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => {}}
        targetDocType="Attendance"
      />
    </div>
  );
};

export default AttendanceCreate;
